import { type FC, memo, useCallback, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import {
  ColoredLabel,
  GradientLabel,
  MenuItem,
  MenuItemIntent,
  MenuSection,
  Slider,
  SymbolIcon,
  TriggerButton,
  TriggerButtonGroup,
  TriggerButtonSize,
} from "widgets";

import style from "../styles/TrackHeader.module.scss";

import type { TrackNgsConfiguration, TrackNgsOrderedBamFilesData } from "../../../graphql.js";
import { setNgsTrackBamFileCustomization } from "../../../redux/modules/genome-browser.js";
import type { IAppState } from "../../../redux/reducer.js";
import type { ReduxDispatch } from "../../../redux/redux-utils.js";
import {
  GENOME_TRACK_COLOR,
  NGS_READS_PILEUP_COLORING,
  NGS_READS_PILEUP_DENSITY,
  TRACK_TYPE,
} from "../../../settings/settings.js";
import { MenuActionWidget, MenuDialog } from "../../core/overlays/index.js";
import { TrackHeader } from "../TrackHeader.js";
import { DEFAULT_NGS_LANES_COUNT } from "./_mod.js";

export interface INgsReadTrackHeaderProps {
  bamFileId: number;
  isTrackHovered?: boolean;
}

const NgsReadTrackHeader: FC<INgsReadTrackHeaderProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  // -- Customization: In the Track Header (+ Redux)
  const ngsConfiguration = useSelector<IAppState, TrackNgsConfiguration>((state) => {
    const track = state.genomeBrowser.tracks.find((tr) => tr.type === TRACK_TYPE.NGS);
    return track?.trackCustomization?.ngsConfiguration;
  });
  const bamFilesConfig: TrackNgsOrderedBamFilesData = useMemo(
    () => ngsConfiguration?.orderedBamFiles?.find((bamFileData) => bamFileData?.bamFileId === props.bamFileId),
    [ngsConfiguration],
  );
  const density =
    (bamFilesConfig.pileupDensity as keyof typeof NGS_READS_PILEUP_DENSITY) || NGS_READS_PILEUP_DENSITY.MEDIUM;
  const pileupIncludeSoftClippedReads = bamFilesConfig.pileupIncludeSoftClippedReads || false;
  const lanesCount = bamFilesConfig.pileupLanesCount || DEFAULT_NGS_LANES_COUNT;
  const pileupColoring =
    (bamFilesConfig.pileupColoring as NGS_READS_PILEUP_COLORING) || NGS_READS_PILEUP_COLORING.DEFAULT;

  const onPileupDensityChange = useCallback(
    (pileupDensity: NGS_READS_PILEUP_DENSITY) =>
      dispatch(setNgsTrackBamFileCustomization(props.bamFileId, { pileupDensity })),
    [dispatch, props.bamFileId],
  );

  const onPileupColoringChange = useCallback(
    (pileupColoring: NGS_READS_PILEUP_COLORING) =>
      dispatch(setNgsTrackBamFileCustomization(props.bamFileId, { pileupColoring })),
    [dispatch, props.bamFileId],
  );

  const onPileupSoftClippedReadsDisplayChange = useCallback(
    (pileupIncludeSoftClippedReads: boolean) => {
      // switch the coloring to default if it was set to soft clipping and the display of soft clipped segments is switched off
      const pileupColoring: string =
        bamFilesConfig.pileupColoring === NGS_READS_PILEUP_COLORING.SOFT_CLIPPING && !pileupIncludeSoftClippedReads
          ? NGS_READS_PILEUP_COLORING.DEFAULT
          : bamFilesConfig.pileupColoring;
      dispatch(setNgsTrackBamFileCustomization(props.bamFileId, { pileupColoring, pileupIncludeSoftClippedReads }));
    },
    [dispatch, bamFilesConfig, props.bamFileId],
  );

  return (
    <TrackHeader
      isTrackHovered={props.isTrackHovered}
      trackIcons={
        <TriggerButtonGroup vertical>
          <MenuActionWidget
            actionWidget={({ toggle }) => (
              <TriggerButton
                icon={"more_vert"}
                label={"Customize Track"}
                onPress={() => toggle()}
                size={TriggerButtonSize.Small}
              />
            )}
          >
            {({ close }) => (
              <MenuDialog
                topWidget={
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      rowGap: 20,
                      margin: "15px -5px 0",
                    }}
                  >
                    <div style={{ margin: "0 10px 10px" }}>
                      <Slider
                        label="Lanes quantity"
                        minValue={10}
                        maxValue={500}
                        defaultValue={[lanesCount]}
                        step={10}
                        icon={"density_small"}
                        onChangeEnd={([c]) => {
                          const lanesCount = c || DEFAULT_NGS_LANES_COUNT; // zero doesn't make sense
                          dispatch(setNgsTrackBamFileCustomization(props.bamFileId, { pileupLanesCount: lanesCount }));
                        }} // Number(undefined) = NoN => falsy
                        tooltip
                      />
                    </div>
                  </div>
                }
                onClose={close}
                aria-label={"Customize"}
              >
                <MenuSection title={"Lanes Density"}>
                  <MenuItem
                    id={"density-compact"}
                    intent={density === NGS_READS_PILEUP_DENSITY.COMPACT ? MenuItemIntent.Active : undefined}
                    onAction={() => onPileupDensityChange(NGS_READS_PILEUP_DENSITY.COMPACT)}
                  >
                    Compact View
                  </MenuItem>
                  <MenuItem
                    id={"density-medium"}
                    intent={density === NGS_READS_PILEUP_DENSITY.MEDIUM ? MenuItemIntent.Active : undefined}
                    onAction={() => onPileupDensityChange(NGS_READS_PILEUP_DENSITY.MEDIUM)}
                  >
                    Default View
                  </MenuItem>
                  <MenuItem
                    id={"density-large"}
                    intent={density === NGS_READS_PILEUP_DENSITY.LARGE ? MenuItemIntent.Active : undefined}
                    onAction={() => onPileupDensityChange(NGS_READS_PILEUP_DENSITY.LARGE)}
                  >
                    Detailed View
                  </MenuItem>
                </MenuSection>
                <MenuSection title={"Read Coloring"}>
                  <MenuItem
                    id={"coloring-default"}
                    intent={pileupColoring === NGS_READS_PILEUP_COLORING.DEFAULT ? MenuItemIntent.Active : undefined}
                    onAction={() => onPileupColoringChange(NGS_READS_PILEUP_COLORING.DEFAULT)}
                  >
                    Default
                  </MenuItem>
                  <MenuItem
                    id={"coloring-strand"}
                    intent={pileupColoring === NGS_READS_PILEUP_COLORING.STRAND ? MenuItemIntent.Active : undefined}
                    onAction={() => onPileupColoringChange(NGS_READS_PILEUP_COLORING.STRAND)}
                    tag={
                      <span className={style.menu_details_list}>
                        <ColoredLabel color={GENOME_TRACK_COLOR.STRAND_PLUS}>
                          <SymbolIcon icon={"add"} size={12} />
                        </ColoredLabel>
                        <ColoredLabel color={GENOME_TRACK_COLOR.STRAND_MINUS}>
                          <SymbolIcon icon={"remove"} size={12} />
                        </ColoredLabel>
                      </span>
                    }
                  >
                    Strand
                  </MenuItem>
                  <MenuItem
                    id={"coloring-pe_strand"}
                    intent={pileupColoring === NGS_READS_PILEUP_COLORING.PE_STRAND ? MenuItemIntent.Active : undefined}
                    onAction={() => onPileupColoringChange(NGS_READS_PILEUP_COLORING.PE_STRAND)}
                    tag={
                      <span className={style.menu_details_list}>
                        <ColoredLabel color={GENOME_TRACK_COLOR.STRAND_PLUS}>
                          <SymbolIcon icon={"add"} size={12} />
                        </ColoredLabel>
                        <ColoredLabel color={GENOME_TRACK_COLOR.STRAND_MINUS}>
                          <SymbolIcon icon={"remove"} size={12} />
                        </ColoredLabel>
                      </span>
                    }
                  >
                    PE Strand
                  </MenuItem>
                  <MenuItem
                    id={"coloring-mapping_quality"}
                    intent={
                      pileupColoring === NGS_READS_PILEUP_COLORING.MAPPING_QUALITY ? MenuItemIntent.Active : undefined
                    }
                    onAction={() => onPileupColoringChange(NGS_READS_PILEUP_COLORING.MAPPING_QUALITY)}
                    tag={
                      <GradientLabel
                        start={GENOME_TRACK_COLOR.GRADIENT_START}
                        stop={GENOME_TRACK_COLOR.GRADIENT_STOP}
                      />
                    }
                  >
                    Mapping Quality
                  </MenuItem>
                  <MenuItem
                    id={"coloring-soft_clipping"}
                    intent={
                      pileupColoring === NGS_READS_PILEUP_COLORING.SOFT_CLIPPING ? MenuItemIntent.Active : undefined
                    }
                    isDisabled={!pileupIncludeSoftClippedReads}
                    onAction={() => onPileupColoringChange(NGS_READS_PILEUP_COLORING.SOFT_CLIPPING)}
                    tag={
                      <span className={style.menu_details_list}>
                        <ColoredLabel color={GENOME_TRACK_COLOR.STRAND_MINUS}>{"Clipped"}</ColoredLabel>
                      </span>
                    }
                  >
                    Soft Clipping
                  </MenuItem>
                  <MenuItem
                    id={"coloring-split_reads"}
                    intent={
                      pileupColoring === NGS_READS_PILEUP_COLORING.SPLIT_READS ? MenuItemIntent.Active : undefined
                    }
                    onAction={() => onPileupColoringChange(NGS_READS_PILEUP_COLORING.SPLIT_READS)}
                    tag={
                      <span className={style.menu_details_list}>
                        <ColoredLabel color={GENOME_TRACK_COLOR.STRAND_MINUS}>{"Split"}</ColoredLabel>
                      </span>
                    }
                  >
                    Split Reads
                  </MenuItem>
                  <MenuItem
                    id={"coloring-read_length"}
                    intent={
                      pileupColoring === NGS_READS_PILEUP_COLORING.READ_LENGTH ? MenuItemIntent.Active : undefined
                    }
                    onAction={() => onPileupColoringChange(NGS_READS_PILEUP_COLORING.READ_LENGTH)}
                    tag={
                      <GradientLabel
                        start={GENOME_TRACK_COLOR.GRADIENT_START}
                        stop={GENOME_TRACK_COLOR.GRADIENT_STOP}
                      />
                    }
                  >
                    Read Length
                  </MenuItem>
                </MenuSection>
                <MenuSection title={"Filters"}>
                  <MenuItem
                    id={"soft-clipped-visibility"}
                    icon={"check"}
                    intent={pileupIncludeSoftClippedReads ? MenuItemIntent.Active : MenuItemIntent.Inactive}
                    onAction={() => onPileupSoftClippedReadsDisplayChange(!pileupIncludeSoftClippedReads)}
                    data-checked={String(pileupIncludeSoftClippedReads)}
                  >
                    Show Soft Clipped Bases
                  </MenuItem>
                </MenuSection>
              </MenuDialog>
            )}
          </MenuActionWidget>
        </TriggerButtonGroup>
      }
    />
  );
};

/// --- Export --- ///
const MemoNgsTrackHeader = memo(NgsReadTrackHeader, shallowEqual);

export { MemoNgsTrackHeader as NgsReadTrackHeader };
